import { EnumUtility } from '@codequality/vue-cli-base';
import BigNumber from 'bignumber.js';
import _ from 'lodash';
import Vue from 'vue';

import {
  Eフェーズ種別,
  Eフリーキャッシュフロー種別,
  E分析非表示フラグ,
  E勘定科目カテゴリ,
  E補助科目フラグ,
  E製造原価科目未使用フラグ,
  E計画表示モード,
  E集計転記科目,
} from '@/openapi/enum';
import { CデータPlbscfItem, ECF集計項目, E固変区分, 勘定科目ListItemResponse, 帳票DynamicConfig } from '@/openapi/user';

import { MONTHS_OF_YEAR } from '@/classes/common/Constants';
import 決算期Utility, { 決算期TableHeader } from '@/classes/common/決算期Utility';
import AbstractデータService, { 帳票データ } from '@/classes/service/AbstractデータService';
import { Plbscf業績予測ListItem } from '@/classes/service/Abstract計画PlbscfService';
import {
  CALC_PROCESS_MAP,
  CalcProcess,
  CodeFilter,
  CodeSumFilter,
  CodeUniqueDivision,
  PlbscfListItemConfig,
  PlbscfType,
  RowCssClass,
  ValueType,
  勘定科目Filter,
  損益_CALC_PROCESS_MAP,
  月次_CALC_PROCESS_MAP,
  計算Type,
} from '@/master/Plbscf';

// ------------------------------------------------------------------
// enum
// ------------------------------------------------------------------

/**
 * 対象年の表示種別
 */
export enum YearItemType {
  blank, // 空欄（グレーのセル）
  readonly, // 入力不可
}

// ------------------------------------------------------------------
// type
// ------------------------------------------------------------------

/**
 * CデータPlbscfItemのvalueをundefined可にしたtype
 */
export type PartialCデータPlbscfItem = Omit<CデータPlbscfItem, 'value'> & Partial<Pick<CデータPlbscfItem, 'value'>>;

/**
 * tデータ用
 */
export type TデータPlbscf = {
  データpk?: number;
  番号: number;
  部門pk?: number;
  items: CデータPlbscfItem[];
};
/**
 * PlbscfYearItem の一部（DBデータと表示情報）
 */
export type PlbscfItemValue = {
  item: PartialCデータPlbscfItem;

  // 年のセルに使用したい値が以下のように異なる
  // ・貸借 かつ 月次データが存在する場合・・・基本、最終月の値
  // ・上記以外の場合・・・合計値
  // → DBに保存している item.value は変わらず合計値として扱いたかったので、計画貸借用に別変数を用意した
  optionValue?: number;
};
/**
 * PlbscfYearValue のうち、DBデータと表示情報を設定
 * ※画面上でいうと、主に色が付いていないセル（ValueType: 勘定科目）
 */
export type PlbscfYearItem = PlbscfItemValue & {
  withMonths?: true; // 月次データを表示する場合、true
  year: number;
  type?: YearItemType; // undefinedの場合、入力可
};
/**
 * PlbscfYearValue のうち、計算結果を設定
 * ※画面上でいうと、主に色が付いているセル（ValueType: calcValue/calcValueReadonly）
 */
export type PlbscfYearResult = {
  year: number;
  calcValue?: number;
  monthlyCalcValues?: number[];
  yCalcValue?: number;
};
/**
 * PlbscfListItem の yearValues（全ての年情報）の一要素
 * ※画面上でいうと、一年分の列が該当
 * 　例）
 * 　・前期
 * 　・0期（年/月ともに）
 * 　・1期（年/月ともに）
 * 　・2期
 * 　...
 */
export type PlbscfYearValue = PlbscfYearItem & PlbscfYearResult;
/**
 * 一行の情報
 */
export type PlbscfListItem = {
  config: PlbscfListItemConfig;
  yearValues: PlbscfYearValue[]; // size：年数分
  勘定科目?: 勘定科目ListItemResponse;

  // 以下、モニタリングで使用
  monitoring?: {
    yearValue: PlbscfYearValue;
  };
};
/**
 * 通常Plbscf一覧
 */
export type PlbscfTypeList = {
  type: PlbscfType;
  header: 決算期TableHeader[]; // size：列数
  list: PlbscfListItem[]; // size：行数
  部門pk?: number;
  // 三表計画の貸借対照表のみ設定
  貸借CalcConfig?: 貸借CalcConfig;
};
/**
 * 三表計画の貸借対照表「計算」ボタン用
 */
export type 貸借CalcConfig = {
  前期Year: number;
  回転期間s: 集計転記科目;
  集計転記科目合計s: 集計転記科目;
};
/**
 * 集計転記科目
 */
export type 集計転記科目Key = E集計転記科目.売上債権 | E集計転記科目.棚卸資産 | E集計転記科目.仕入債務;
export type 集計転記科目 = {
  [key in 集計転記科目Key]: number;
};
/**
 * 計算Codeと計算結果を紐づけるMap
 */
export type CalcResultMap = Map<string, CalcResult>;
/**
 * CalcProcess + 計算結果
 */
type CalcResult = CalcProcess & {
  results?: (PlbscfYearResult & { 部門pk?: number })[];
};
/**
 * テンプレート行設定
 */
export type PrintTemplateConfig = {
  type: PlbscfType;
  list: PrintTemplateConfigList;
};
/**
 * テンプレート行設定（表）
 */
export type PrintTemplateConfigList = {
  header: number;
  subheader?: number;
  row: PrintTemplateConfigRow; // PlbscfListItemConfig.unitNotYenに値が無い場合こちらを参照
  unitNotYenRow?: Partial<PrintTemplateConfigRow>; // PlbscfListItemConfig.unitNotYenに値がある場合こちらを参照
};
/**
 * テンプレート行設定（本文）
 */
type PrintTemplateConfigRow = {
  body: number;
  title?: number;
  specialHeader?: number;
  header?: number;
  subHeader?: number;
  itemFirst?: number;
  itemSecond?: number;
  diff?: number;
  grey?: number;
  cfSubHeader?: number;
};

export type KeyOldLoan = '短期' | '長期' | '役員';

// ------------------------------------------------------------------
// const
// ------------------------------------------------------------------

/**
 * 科目別計画明細Config数
 */
export const 科目別計画明細_CONFIG_COUNT = 10;

/**
 * テンプレート行設定
 */
export const PRINT_TEMPLATE_CONFIGS: PrintTemplateConfig[] = [
  {
    type: PlbscfType.貸借対照表,
    list: {
      header: 0,
      row: {
        body: 1,
        specialHeader: 2,
        header: 3,
        subHeader: 4,
        itemFirst: 5,
        itemSecond: 6,
        diff: 7,
      },
    },
  },
  {
    type: PlbscfType.損益計算書,
    list: {
      header: 0,
      row: {
        body: 1,
        title: 2,
        specialHeader: 3,
        header: 5,
        subHeader: 6,
        itemFirst: 7,
        itemSecond: 8,
        grey: 9,
      },
      unitNotYenRow: {
        specialHeader: 4,
      },
    },
  },
  {
    type: PlbscfType.CF計算書,
    list: {
      header: 0,
      row: {
        body: 1,
        header: 2,
        diff: 3,
        cfSubHeader: 4,
      },
    },
  },
  {
    type: PlbscfType.変動損益,
    list: {
      header: 0,
      row: {
        body: 1,
        header: 3,
        itemSecond: 4,
        diff: 5,
      },
      unitNotYenRow: {
        body: 2,
      },
    },
  },
  {
    type: PlbscfType.運転資金,
    list: {
      header: 0,
      row: {
        body: 1,
        header: 3,
      },
      unitNotYenRow: {
        body: 2,
      },
    },
  },
  {
    type: PlbscfType.FCF,
    list: {
      header: 0,
      row: {
        body: 1,
        itemFirst: 2,
      },
      unitNotYenRow: {
        itemFirst: 3,
      },
    },
  },
];

export default abstract class AbstractPlbscfService<T> extends AbstractデータService<T> {
  /** parseで渡された tデータ */
  protected rawTデータPlbscfs: TデータPlbscf[] = [];

  /** 計算用データ */
  protected calcTypeLists: PlbscfTypeList[] = [];

  /** 計算格納用Map */
  protected allCalcResultMap: CalcResultMap | null = null;

  /** 計算格納用Map（月次） */
  protected monthlyCalcResultMap: CalcResultMap | null = null;

  // ------------------------------------------------------------------
  // constructor
  // ------------------------------------------------------------------

  public constructor(eフェーズ種別: Eフェーズ種別 | null = null) {
    super(eフェーズ種別);
    this.setBnRoundingMode();
  }

  // ------------------------------------------------------------------
  // getter/setter
  // ------------------------------------------------------------------

  /**
   * parseで渡された tデータ
   * @returns
   */
  public get getTデータPlbscfs(): TデータPlbscf[] {
    return this.rawTデータPlbscfs;
  }

  // ------------------------------------------------------------------
  // public static methods
  // ------------------------------------------------------------------

  /**
   * 年の値を返却する（優先度：optionValue > value > calcValue）
   * @param yearValue
   * @returns
   */
  public static getYearValue(yearValue: PlbscfYearValue, isYConfig = false, isCF計算書 = false): number {
    if (yearValue.optionValue !== undefined) return yearValue.optionValue;
    if ((isCF計算書 || this.計画書.c計画表示モード === E計画表示モード.年次計画) && isYConfig) {
      if (yearValue.item.yValue !== undefined) return yearValue.item.yValue;
      if (yearValue.yCalcValue !== undefined) return yearValue.yCalcValue;
    }
    if (yearValue.item.value !== undefined) return yearValue.item.value;
    if (yearValue.calcValue !== undefined) return yearValue.calcValue;
    return 0;
  }

  /**
   * 値なしか
   * @param config
   * @param yearValue
   * @returns
   */
  public static isNoValue(config: PlbscfListItemConfig, yearValue: PlbscfYearValue): boolean {
    return config.valueType === ValueType.noValue || yearValue.type === YearItemType.blank;
  }

  /**
   * 編集可能か
   * @param config
   * @param yearValue
   * @returns
   */
  public static isEditable(config: PlbscfListItemConfig, yearValue: PlbscfYearValue): boolean {
    return [ValueType.勘定科目, ValueType.calcValue].includes(config.valueType) && yearValue.type === undefined;
  }

  /**
   * 年/月の値を返却
   * （年の優先度：optionValue > value）
   *
   * @param iv
   * @param monthlyIndex
   * @returns
   */
  public static getOptionValueIfExistOrDBValue(
    iv?: PlbscfItemValue,
    monthlyIndex?: number,
    isByYear = false,
    isByMonth = true,
    lastItem = true
  ): number | undefined {
    if (iv === undefined) return undefined;
    if (monthlyIndex === undefined) {
      // 年
      if (iv.optionValue !== undefined) return iv.optionValue;
      if ((this.計画書.c計画表示モード === E計画表示モード.年次計画 && isByMonth) || isByYear) {
        if (iv.item.yValue !== undefined) return iv.item.yValue;
      }
      if (this.計画書.c計画表示モード === E計画表示モード.年次計画 && !isByMonth && !lastItem) {
        return 0;
      }
      if (iv.item.value !== undefined) return iv.item.value;
    } else {
      // 月
      if (iv.item.monthlyValues !== undefined) return iv.item.monthlyValues[monthlyIndex];
    }
  }

  // ------------------------------------------------------------------
  // protected methods
  // ------------------------------------------------------------------

  /**
   * typeListsに計算結果を格納
   * @returns
   */
  protected setValuesByCalcResult(): void {
    this.allCalcResultMap = _.cloneDeep(CALC_PROCESS_MAP);
    this.monthlyCalcResultMap = _.cloneDeep(月次_CALC_PROCESS_MAP);
    const 損益CalcResultMap: CalcResultMap = _.cloneDeep(損益_CALC_PROCESS_MAP);

    const filteredTypeList = this.calcTypeLists.filter((t) =>
      [PlbscfType.貸借対照表, PlbscfType.損益計算書, PlbscfType.CF計算書].includes(t.type)
    );
    // tデータごとに算出、値を設定
    for (const tデータPlbscf of this.rawTデータPlbscfs) {
      const year = tデータPlbscf.番号;
      const 部門pk = tデータPlbscf.部門pk;
      const itemValues = this.getFilteredItemValues(filteredTypeList, year, 部門pk);
      const isMonthly = this.isMonthlyByYear(year);

      const 年次Map = 部門pk === undefined ? this.allCalcResultMap : 損益CalcResultMap;
      const 月次Map = 部門pk === undefined ? this.monthlyCalcResultMap : 損益CalcResultMap;

      // 年の計算
      this.setCalcResultMapValue(年次Map, itemValues, year, undefined, 部門pk, true);
      if (isMonthly) {
        // 月の計算
        for (let i = 0; i < MONTHS_OF_YEAR; i++) this.setCalcResultMapValue(月次Map, itemValues, year, i, 部門pk);
      }

      const typeLists = this.calcTypeLists.filter((typeList) => typeList.部門pk === 部門pk);
      // 現在の年のyearValueに、計算結果を設定
      for (const typeList of typeLists) {
        for (const listItem of typeList.list) {
          if (!this.isCodeFilter(listItem.config.valueType, listItem.config.filter)) continue;
          const code = listItem.config.filter.code;
          const foundYearValue = listItem.yearValues.find((yv) => yv.year === year);
          if (foundYearValue === undefined || foundYearValue.type === YearItemType.blank || !年次Map.has(code))
            continue;

          // 年の値を設定
          const calcResult = 年次Map
            .get(code)!
            .results!.find((result) => result.year === foundYearValue.year && result.部門pk === 部門pk)!;
          foundYearValue.calcValue = calcResult.calcValue!;
          foundYearValue.yCalcValue = calcResult.yCalcValue!;

          // 月の値を設定
          if (isMonthly && 月次Map.has(code)) {
            const 月次CalcResult = 月次Map
              .get(code)!
              .results!.find((result) => result.year === foundYearValue.year && result.部門pk === 部門pk)!;
            for (let i = 0; i < MONTHS_OF_YEAR; i++)
              // 配列の変更のみの場合、画面でリアクティブにならないので Vue.set にした
              Vue.set(foundYearValue.monthlyCalcValues!, i, 月次CalcResult.monthlyCalcValues![i]);
          }
        }
      }
    }
  }

  /**
   * CalcResultMapに計算結果を設定する
   * @param calcResultMap
   * @param itemValues
   * @param year
   * @param monthlyIndex
   * @param 部門pk
   */
  protected setCalcResultMapValue(
    calcResultMap: CalcResultMap,
    itemValues: PlbscfItemValue[],
    year: number,
    monthlyIndex?: number,
    部門pk?: number,
    getYear = false
  ): void {
    const 表示勘定科目s = this.表示勘定科目s(this.勘定科目s);
    for (const [code, calcResult] of calcResultMap) {
      let result = 0;
      let yResult = 0;
      switch (calcResult.config.計算Type) {
        case 計算Type.勘定科目Sum:
          // 勘定科目の合計
          result = this.sumBy勘定科目(itemValues, 表示勘定科目s, calcResult.config.勘定科目Sum!, monthlyIndex);
          if (monthlyIndex === undefined && getYear) {
            yResult = this.sumBy勘定科目(
              itemValues,
              表示勘定科目s,
              calcResult.config.勘定科目Sum!,
              monthlyIndex,
              getYear
            );
          }
          break;
        case 計算Type.codeSum:
          // 計算Codeの合計
          result = this.sumByCode(calcResultMap, itemValues, calcResult.config.codeSum!, year, monthlyIndex, 部門pk);
          if (monthlyIndex === undefined && getYear) {
            yResult = this.sumByCode(
              calcResultMap,
              itemValues,
              calcResult.config.codeSum!,
              year,
              monthlyIndex,
              部門pk,
              getYear
            );
          }
          break;
        case 計算Type.codeUnique:
          // code別計算
          // codeUniqueが設定されている場合、共通除算処理を行う
          if (calcResult.config.codeUnique !== undefined) {
            result = this.divideByCodeUniqueDivision(
              calcResultMap,
              itemValues,
              calcResult.config.codeUnique.division,
              code,
              year,
              monthlyIndex,
              部門pk
            );
            if (monthlyIndex === undefined && getYear) {
              yResult = this.divideByCodeUniqueDivision(
                calcResultMap,
                itemValues,
                calcResult.config.codeUnique.division,
                code,
                year,
                monthlyIndex,
                部門pk,
                getYear
              );
            }
          } else {
            // codeUniqueが設定されていない場合、codeごとに個別に計算する
            switch (code) {
              case 'N315':
                result = this.getValueByCode(calcResultMap, itemValues, 'N317', year - 1, monthlyIndex, 部門pk);
                if (monthlyIndex === undefined && getYear) {
                  yResult = this.getValueByCode(
                    calcResultMap,
                    itemValues,
                    'N317',
                    year - 1,
                    monthlyIndex,
                    部門pk,
                    getYear
                  );
                }
                break;
              case 'N416':
                const N40xFilter: 勘定科目Filter = {
                  column: 'cCF集計項目',
                  values: [ECF集計項目.支払利息等],
                };
                result = new BigNumber(this.sumBy勘定科目(itemValues, 表示勘定科目s, N40xFilter, monthlyIndex))
                  .times(-1)
                  .toNumber();
                if (monthlyIndex === undefined && getYear) {
                  yResult = new BigNumber(
                    this.sumBy勘定科目(itemValues, 表示勘定科目s, N40xFilter, monthlyIndex, getYear)
                  )
                    .times(-1)
                    .toNumber();
                }
                break;
              case 'N433':
                const diffYear = 決算期Utility.diffYearFrom0期(year);
                switch (diffYear) {
                  case -5:
                    // 5期前：何もしない
                    break;
                  case -4:
                    // 4期前：N1
                    result = this.getValueByCode(calcResultMap, itemValues, 'N1', year - 1, monthlyIndex, 部門pk);
                    if (monthlyIndex === undefined && getYear) {
                      yResult = this.getValueByCode(
                        calcResultMap,
                        itemValues,
                        'N1',
                        year - 1,
                        monthlyIndex,
                        部門pk,
                        getYear
                      );
                    }
                    break;
                  default:
                    // 上記以外：N434
                    result = this.getValueByCode(calcResultMap, itemValues, 'N434', year - 1, monthlyIndex, 部門pk);
                    const diffYearPlus = 決算期Utility.diffYearFrom0期(year, true);
                    if (monthlyIndex === undefined && getYear && diffYearPlus > 1) {
                      yResult = this.getValueByCode(
                        calcResultMap,
                        itemValues,
                        'N434',
                        year - 1,
                        monthlyIndex,
                        部門pk,
                        getYear
                      );
                    } else {
                      yResult = result;
                    }
                    break;
                }
                break;
              case 'N508':
                const N508Filter: 勘定科目Filter = {
                  カテゴリs: [E勘定科目カテゴリ.損益, E勘定科目カテゴリ.製造原価],
                  column: 'c固変区分',
                  values: [E固変区分.固定費],
                };
                result = new BigNumber(this.sumBy勘定科目(itemValues, 表示勘定科目s, N508Filter, monthlyIndex))
                  .minus(this.getValueByCode(calcResultMap, itemValues, 'N218', year, monthlyIndex, 部門pk))
                  .plus(this.getValueByCode(calcResultMap, itemValues, 'N219', year, monthlyIndex, 部門pk))
                  .toNumber();
                if (monthlyIndex === undefined && getYear) {
                  yResult = new BigNumber(
                    this.sumBy勘定科目(itemValues, 表示勘定科目s, N508Filter, monthlyIndex, getYear)
                  )
                    .minus(this.getValueByCode(calcResultMap, itemValues, 'N218', year, monthlyIndex, 部門pk, getYear))
                    .plus(this.getValueByCode(calcResultMap, itemValues, 'N219', year, monthlyIndex, 部門pk, getYear))
                    .toNumber();
                }
                break;
              case 'N703':
                const N703Filter: 勘定科目Filter = {
                  カテゴリs: [E勘定科目カテゴリ.損益],
                  column: 'c集計転記科目',
                  values: [E集計転記科目.法人税等],
                };
                result = new BigNumber(this.sumBy勘定科目(itemValues, 表示勘定科目s, N703Filter, monthlyIndex))
                  .times(-1)
                  .toNumber();
                if (monthlyIndex === undefined && getYear) {
                  yResult = new BigNumber(
                    this.sumBy勘定科目(itemValues, 表示勘定科目s, N703Filter, monthlyIndex, getYear)
                  )
                    .times(-1)
                    .toNumber();
                }
                break;
              case 'N704':
                // 以下を満たす場合はCF計算書の「FCF」を、それ以外の場合は通常の「FCF：A」を計算し設定
                //  ・t計画書データ.cフリーキャッシュフロー種別 === 1: CF計算書
                //  ・5期前の年ではない（CF計算書には5期前が存在せずコピー不可のため除外）
                const isCF計算書 =
                  AbstractデータService.計画書.cフリーキャッシュフロー種別 === Eフリーキャッシュフロー種別.CF計算書 &&
                  決算期Utility.diffYearFrom0期(year) !== -5;
                const CodeSumFilters: CodeSumFilter[] = isCF計算書
                  ? [{ code: 'N437' }]
                  : [{ code: 'N701' }, { code: 'N702' }, { code: 'N703' }];
                result = this.sumByCode(calcResultMap, itemValues, CodeSumFilters, year, monthlyIndex, 部門pk);
                if (monthlyIndex === undefined && getYear) {
                  yResult = this.sumByCode(
                    calcResultMap,
                    itemValues,
                    CodeSumFilters,
                    year,
                    monthlyIndex,
                    部門pk,
                    getYear
                  );
                }
                break;
              default:
                break;
            }
          }
          break;
      }
      // 「0 × (-1)」のような計算時、「-0」とマイナスが付いて表示されてしまうため、マイナスを消すため「+ 0」する
      result += 0;
      yResult += 0;

      // 現在の年の CalcResult がまだなかったら作成
      if (calcResult.results === undefined) calcResult.results = [];
      let r = calcResult.results.find((result) => result.year === year && result.部門pk === 部門pk);
      if (r === undefined) {
        r = {
          year,
          部門pk,
        };
        calcResult.results.push(r);
      }
      // 計算結果を設定
      if (monthlyIndex === undefined) {
        // 年
        r.calcValue = result;
        r.yCalcValue = yResult;
        if (r.year > 決算期Utility.get計画0期の決算年() + 1) {
          r.calcValue = yResult;
        }
      } else {
        // 月
        if (r.monthlyCalcValues === undefined) r.monthlyCalcValues = new Array(MONTHS_OF_YEAR).fill(0);
        r.monthlyCalcValues[monthlyIndex] = result;
      }

      calcResultMap.set(code, calcResult);
    }
  }

  /**
   * 損益合計の計算結果を取得する
   * @returns
   */
  protected getTotal損益CalcResultMap(): CalcResultMap {
    const total損益CalcResultMap: CalcResultMap = _.cloneDeep(損益_CALC_PROCESS_MAP);

    const filteredTypeList = this.calcTypeLists.filter((t) => [PlbscfType.損益計算書].includes(t.type));
    const filteredTデータ = this.rawTデータPlbscfs.filter((t) => t.部門pk === undefined);
    for (const tデータPlbscf of filteredTデータ) {
      const year = tデータPlbscf.番号;
      const itemValues = this.getFilteredItemValues(filteredTypeList, year);
      // 年の計算
      const isYearConfig = 決算期Utility.checkE計画表示モード() && 決算期Utility.diffYearFrom0期(year, true) > 0;
      this.setCalcResultMapValue(total損益CalcResultMap, itemValues, year, undefined, undefined, isYearConfig);
      if (this.isMonthlyByYear(year)) {
        // 月の計算
        for (let i = 0; i < MONTHS_OF_YEAR; i++)
          this.setCalcResultMapValue(total損益CalcResultMap, itemValues, year, i);
      }
    }
    return total損益CalcResultMap;
  }

  /**
   * 型チェック：CodeFilter
   * @param type
   * @param filter
   * @returns
   */
  protected isCodeFilter(type: ValueType, filter: 勘定科目Filter | CodeFilter | undefined): filter is CodeFilter {
    return [ValueType.calcValue, ValueType.calcValueReadonly].includes(type);
  }

  /**
   * 型チェック：勘定科目Filter
   * @param type
   * @param filter
   * @returns
   */
  protected is勘定科目Filter(
    type: ValueType,
    filter: 勘定科目Filter | CodeFilter | undefined
  ): filter is 勘定科目Filter {
    return type === ValueType.勘定科目;
  }

  /**
   * 勘定科目をフィルタリング
   * @param 勘定科目s
   * @param 勘定科目Filter
   * @returns
   */
  protected filtered勘定科目s(
    勘定科目s: 勘定科目ListItemResponse[],
    勘定科目Filter: 勘定科目Filter
  ): 勘定科目ListItemResponse[] {
    return 勘定科目s.filter((勘) => {
      if (勘定科目Filter.カテゴリs !== undefined && !勘定科目Filter.カテゴリs.includes(勘.cカテゴリ)) return false;
      const value = 勘[勘定科目Filter.column];
      return value !== undefined && 勘定科目Filter.values.includes(value);
    });
  }

  /**
   * 表示用勘定科目を分析非表示フラグでfilter
   * @param 勘定科目s
   * @returns
   */
  protected 表示勘定科目s(勘定科目s: 勘定科目ListItemResponse[]): 勘定科目ListItemResponse[] {
    return 勘定科目s.filter(
      (勘) => 勘.c分析非表示フラグ === E分析非表示フラグ.表示 && 勘.c補助科目フラグ === E補助科目フラグ.通常
    );
  }

  /**
   * 三表用の帳票データを整形
   *
   * @param plbscfType
   * @param typeList
   * @returns
   */
  protected createPlbscf帳票データ(
    plbscfType: PlbscfType,
    typeList: { header: 決算期TableHeader[]; list: Plbscf業績予測ListItem[] },
    isPls = false,
    isCF計算書 = false
  ): 帳票データ {
    const 計画0期Year = 決算期Utility.get計画0期の決算年(true);
    const printTemplateConfig = PRINT_TEMPLATE_CONFIGS.find((p) => p.type === plbscfType)!;

    // ラベルにカテゴリ列が存在するかどうか
    const foundCategoryRow = typeList.list.find((listItem) => listItem.config.label.category !== undefined);
    const categoryExists = foundCategoryRow !== undefined;

    // 以下帳票データ整形
    // 初行設定込みで初期化
    const templateRows: number[] = [printTemplateConfig.list.header];
    const headerLabels: string[] = [AbstractデータService.getMoneyUnitLabel(), ''];
    if (categoryExists) headerLabels.push('');
    const values: unknown[][] = [[...headerLabels, ...typeList.header.map((h) => h.label)]];
    let 比率index = -1;

    // 行でループ
    typeList.list.forEach((listItem) => {
      const config = listItem.config;
      templateRows.push(this.getTemplateConfig(printTemplateConfig.list, config));
      const datas = this.createConfigLabels(config, categoryExists);

      for (const yearValue of listItem.yearValues) {
        datas.push(
          this.getFormatValueByType(config, yearValue, undefined, isPls && yearValue.year > 計画0期Year, isCF計算書)
        );
      }

      // 業績予測が存在する場合は追加
      const 業績予測 = listItem.業績予測;
      if (業績予測 !== undefined) {
        datas.push(
          業績予測.差異 === undefined ? '' : this.getMoneyUnitValue(業績予測.差異),
          業績予測.比率 === undefined ? '' : 業績予測.比率 + '%'
        );
        比率index = datas.length - 1;
        for (let i = 0; i < MONTHS_OF_YEAR; i++) {
          const yearValue = listItem.yearValues[listItem.yearValues.length - 1];
          datas.push(
            this.getFormatValueByType(config, yearValue, i, isPls && yearValue.year > 計画0期Year, isCF計算書)
          );
        }
      }

      // 一行追加
      values.push(datas);
    });

    return {
      templateRows,
      values,
      dynamicConfigs: this.create帳票DynamicConfig(typeList.header, headerLabels.length, 比率index),
    };
  }

  /**
   * 項目ラベルの帳票データを作成
   *
   * @param config
   * @param categoryExists
   * @returns
   */
  protected createConfigLabels(config: PlbscfListItemConfig, categoryExists = false): (string | number)[] {
    const label = config.label;
    const datas: (string | number)[] = [];
    if (categoryExists) {
      datas.push(label.category === undefined ? '' : label.category);
    }
    if (label.hierarchy === 0) {
      datas.push(label.name);
      datas.push('');
    } else {
      datas.push('');
      datas.push(label.name);
    }
    return datas;
  }

  /**
   * 帳票の列の動的設定を作成
   *
   * @param header
   * @param staticColumnCount
   * @param 比率index
   * @returns
   */
  protected create帳票DynamicConfig(
    header: 決算期TableHeader[],
    staticColumnCount: number,
    比率index?: number
  ): 帳票DynamicConfig[] {
    return [
      {
        columns: [
          ..._.range(staticColumnCount),
          ...header.map((h, index) =>
            h.isSummary
              ? staticColumnCount + index === 比率index
                ? staticColumnCount + 2
                : staticColumnCount + 1
              : staticColumnCount
          ),
        ],
      },
    ];
  }

  /**
   * TemplateConfigを返却する
   * @param templateConfigList
   * @param plbscfListItemConfig
   * @returns
   */
  protected getTemplateConfig(
    templateConfigList: PrintTemplateConfigList,
    plbscfListItemConfig: PlbscfListItemConfig
  ): number {
    // 通貨では無い時、専用のRow情報を参照
    const row =
      plbscfListItemConfig.unitNotYen !== undefined ? templateConfigList.unitNotYenRow! : templateConfigList.row;
    let config = row.body!;
    switch (plbscfListItemConfig.rowCssClass) {
      case undefined:
        break;
      case RowCssClass.itemRow:
        config = plbscfListItemConfig.label.hierarchy === 0 ? row.itemFirst! : row.itemSecond!;
        break;
      default:
        const enumKey = EnumUtility.valueToKey(RowCssClass, plbscfListItemConfig.rowCssClass);
        const configKey = enumKey.substring(0, enumKey.length - 3);
        config = row[configKey as keyof PrintTemplateConfigRow]!;
        break;
    }
    return config;
  }

  /**
   * ValueTypeに応じて値を取得し、帳票用にフォーマット
   *
   * @param config
   * @param yearValue
   * @param monthlyIndex
   * @returns
   */
  protected getFormatValueByType(
    config: PlbscfListItemConfig,
    yearValue: PlbscfYearValue,
    monthlyIndex?: number,
    isPls = false,
    isCF計算書 = false
  ): string | number {
    if (yearValue.type === YearItemType.blank || config.valueType === ValueType.noValue) return '';
    // ValueTypeに応じて値を設定
    let value = null;
    switch (config.valueType) {
      case ValueType.calcValue:
      case ValueType.勘定科目:
        value =
          monthlyIndex === undefined
            ? AbstractPlbscfService.getYearValue(yearValue, isPls, isCF計算書)
            : yearValue.item.monthlyValues![monthlyIndex];
        break;
      case ValueType.calcValueReadonly:
        value =
          monthlyIndex === undefined
            ? isPls && (AbstractPlbscfService.計画書.c計画表示モード === E計画表示モード.年次計画 || isCF計算書)
              ? yearValue.yCalcValue
              : yearValue.calcValue
            : yearValue.monthlyCalcValues![monthlyIndex];
        break;
    }
    return this.getPrintFormatValue(config, value);
  }

  /**
   * 数値を帳票用にフォーマットして返却する
   *
   * @param config
   * @param value
   * @returns
   */
  protected getPrintFormatValue(config: PlbscfListItemConfig, value?: number): string | number {
    if (config.unitNotYen === undefined) {
      return this.getMoneyUnitValue(value);
    } else {
      // 金額以外
      return value + config.unitNotYen;
    }
  }

  /**
   * 対象計算Codeの値を取得
   *
   * @param calcResultMap
   * @param itemValues
   * @param code
   * @param year
   * @param monthlyIndex
   * @param 部門pk
   * @returns
   */
  protected getValueByCode(
    calcResultMap: CalcResultMap,
    itemValues: PlbscfItemValue[],
    code: string,
    year: number,
    monthlyIndex?: number,
    部門pk?: number,
    isYear = false
  ): number {
    // 画面からの入力値が存在したら、入力値を返却
    const foundIv = itemValues.find((iv) => iv.item.計算Code === code);
    const value = AbstractPlbscfService.getOptionValueIfExistOrDBValue(foundIv, monthlyIndex, isYear, isYear);
    if (value !== undefined) return value;

    // 入力値が存在しない場合、計算結果を返却
    if (!calcResultMap.has(code)) return 0;
    const foundResult = calcResultMap
      .get(code)!
      .results?.find((result) => result.year === year && result.部門pk === 部門pk);
    if (foundResult === undefined) return 0;
    return monthlyIndex === undefined
      ? isYear
        ? foundResult.yCalcValue!
        : foundResult.calcValue!
      : foundResult.monthlyCalcValues![monthlyIndex];
  }

  /**
   * 計算用PlbscfYearValueを絞り込み
   * @param typeList
   * @param 部門pk
   * @returns
   */
  protected filterYearValues(typeList: PlbscfTypeList[], 部門pk?: number): PlbscfYearValue[] {
    return typeList
      .filter((t) => t.部門pk === 部門pk)
      .flatMap((t) => t.list)
      .flatMap((l) => l.yearValues);
  }

  /**
   * 指定年に紐づく計算用PlbscfYearValueを絞り込み
   * @param yearValues
   * @param year
   * @returns
   */
  protected filterYearValuesByYear(yearValues: PlbscfYearValue[], year: number): PlbscfYearValue[] {
    return yearValues.filter(
      (yv) =>
        yv.year === year &&
        (yv.item.value !== undefined || yv.item.yValue != undefined) &&
        yv.type !== YearItemType.blank &&
        (yv.item.科目別計画明細No === undefined || yv.item.科目別計画明細No === 科目別計画明細_CONFIG_COUNT)
    );
  }

  /**
   * 0でない値が存在する行を返却
   * @param list
   * @returns
   */
  protected filterNotZero<T extends PlbscfListItem>(list: T[]): T[] {
    return list.filter((listItem) => {
      if (this.isTargetToFilterNotZero(listItem.config)) return true;

      for (const yv of listItem.yearValues) {
        if (this.isNotZeroYearValue(yv)) return true;
      }
      const monitoringYv = listItem.monitoring?.yearValue;
      if (monitoringYv !== undefined && this.isNotZeroYearValue(monitoringYv)) {
        return true;
      }
      return false;
    });
  }

  /**
   * 製造原価科目未使用時、製造原価の項目をフィルタリング
   * @param list
   * @returns
   */
  protected filter製造原価科目未使用(list: PlbscfListItem[]): PlbscfListItem[] {
    return AbstractデータService.事業所.c製造原価科目未使用フラグ === E製造原価科目未使用フラグ.使用しない
      ? list.filter((listItem) =>
          listItem.config.valueType === ValueType.勘定科目
            ? listItem.勘定科目!.cカテゴリ !== E勘定科目カテゴリ.製造原価
            : listItem.config.勘定科目カテゴリ !== E勘定科目カテゴリ.製造原価
        )
      : list;
  }

  /**
   * cフリーキャッシュフロー種別に応じて、項目をフィルタリング
   *
   * @param list
   * @returns
   */
  protected filterFCF種別(list: PlbscfListItem[]): PlbscfListItem[] {
    return AbstractデータService.計画書.cフリーキャッシュフロー種別 === Eフリーキャッシュフロー種別.CF計算書
      ? list.filter((listItem) => {
          if (!this.isCodeFilter(listItem.config.valueType, listItem.config.filter)) return true;
          return !['N701', 'N702', 'N703'].includes(listItem.config.filter.code);
        })
      : list;
  }

  /**
   * PlbscfYearValue に、0でない値が存在するか
   * @param yv
   * @returns
   */
  protected isNotZeroYearValue(yv: PlbscfYearValue): boolean {
    if (this.isNotZeroYearItem(yv)) return true;

    if (yv.item.value === undefined) return this.isNotZeroValue(yv.calcValue, yv.monthlyCalcValues);
    return false;
  }

  /**
   * PlbscfYearItem に、0でない値が存在するか
   * @param yi
   * @returns
   */
  protected isNotZeroYearItem(yi: PlbscfYearItem): boolean {
    return this.isNotZeroValue(yi.item.value, yi.item.monthlyValues);
  }

  /**
   * 0でない値が存在する行を返却する際の対象か
   * @param config
   * @returns
   */
  protected isTargetToFilterNotZero(config: PlbscfListItemConfig): boolean {
    return (
      config.label.category !== undefined ||
      config.valueType === ValueType.noValue ||
      (config.valueType !== ValueType.勘定科目 && config.rowCssClass !== undefined)
    );
  }

  /**
   * 指定年に月次データがあるかどうか
   *
   * @param year
   * @returns
   */
  protected isMonthlyByYear(year: number): boolean {
    return 決算期Utility.is計画01(year);
  }

  /**
   * 共通除算処理
   *
   * @param numerator
   * @param denominator
   * @param division
   * @returns
   */
  protected divide(numerator: number, denominator: number, division: CodeUniqueDivision): number {
    return this.calcPercentByBigNumber(
      numerator,
      division.isPercent ? 100 : 1,
      denominator,
      division.roundingMode!,
      division.decimalPlaces
    );
  }

  // ------------------------------------------------------------------
  // private methods
  // ------------------------------------------------------------------

  /**
   * 勘定科目の合計
   * @param itemValues
   * @param 勘定科目s
   * @param 勘定科目Filter
   * @param monthlyIndex
   * @returns
   */
  private sumBy勘定科目(
    itemValues: PlbscfItemValue[],
    勘定科目s: 勘定科目ListItemResponse[],
    勘定科目Filter: 勘定科目Filter,
    monthlyIndex?: number,
    isYear = false
  ): number {
    let sum = 0;

    // 検索条件で勘定科目をフィルタリング
    const filtered勘定科目s = this.filtered勘定科目s(勘定科目s, 勘定科目Filter);
    // 勘定科目に紐づくデータをフィルタリング
    const filteredItemValues = itemValues.filter((iv) => {
      const found勘定科目 = filtered勘定科目s.find((勘) => 勘.pk === iv.item.勘定科目Pk);
      return found勘定科目 !== undefined;
    });
    for (const iv of filteredItemValues) {
      let value = AbstractPlbscfService.getOptionValueIfExistOrDBValue(iv, monthlyIndex, isYear, isYear);
      if (value === undefined) value = 0;
      sum = new BigNumber(sum).plus(value).toNumber();
    }

    return sum;
  }

  /**
   * 計算Codeの合計
   * @param calcResultMap
   * @param itemValues
   * @param CodeSumFilters
   * @param year
   * @param monthlyIndex
   * @param 部門pk
   * @returns
   */
  private sumByCode(
    calcResultMap: CalcResultMap,
    itemValues: PlbscfItemValue[],
    CodeSumFilters: CodeSumFilter[],
    year: number,
    monthlyIndex?: number,
    部門pk?: number,
    isYear = false
  ): number {
    let sum = 0;

    for (const CodeSumFilter of CodeSumFilters) {
      if (!calcResultMap.has(CodeSumFilter.code)) continue;
      // 係数算出
      const coefficient = CodeSumFilter.coefficient === undefined ? 1 : CodeSumFilter.coefficient;
      const y = CodeSumFilter.beforeYearCount === undefined ? year : year - CodeSumFilter.beforeYearCount;
      const value = new BigNumber(
        this.getValueByCode(calcResultMap, itemValues, CodeSumFilter.code, y, monthlyIndex, 部門pk, isYear)
      ).times(coefficient);
      sum = value.plus(sum).toNumber();
    }

    return sum;
  }

  /**
   * CodeUniqueDivisionの設定に従って除算
   * @param calcResultMap
   * @param itemValues
   * @param division
   * @param code
   * @param year
   * @param monthlyIndex
   * @param 部門pk
   * @returns
   */
  private divideByCodeUniqueDivision(
    calcResultMap: CalcResultMap,
    itemValues: PlbscfItemValue[],
    division: CodeUniqueDivision,
    code: string,
    year: number,
    monthlyIndex?: number,
    部門pk?: number,
    isYear = false
  ): number {
    // roundingModeの設定がない場合、t事業所.c実績値及び計画値端数処理から設定
    if (division.roundingMode === undefined) {
      division.roundingMode = this.bnRoundingMode.実績計画;
    }
    // 共通除算処理の実行
    const numeratorValue = isYear
      ? this.getValueByCode(calcResultMap, itemValues, division.numerator, year, monthlyIndex, 部門pk, true)
      : this.getValueByCode(calcResultMap, itemValues, division.numerator, year, monthlyIndex, 部門pk);

    const denominatorValue = isYear
      ? this.getValueByCode(calcResultMap, itemValues, division.denominator, year, monthlyIndex, 部門pk, true)
      : this.getValueByCode(calcResultMap, itemValues, division.denominator, year, monthlyIndex, 部門pk);

    switch (code) {
      case 'N601':
        return this.divide(numeratorValue, 12, division);
      case 'N606':
      case 'N608':
        const N60xFilter: CodeSumFilter[] = code === 'N606' ? [{ code: 'N602' }] : [{ code: 'N604' }];
        if (isYear) {
          return this.divide(
            this.sumByCode(calcResultMap, itemValues, N60xFilter, year, monthlyIndex, 部門pk, true),
            denominatorValue,
            division
          );
        }
        return this.divide(
          this.sumByCode(calcResultMap, itemValues, N60xFilter, year, monthlyIndex, 部門pk),
          denominatorValue,
          division
        );
      case 'N708':
        const N708Filter: CodeSumFilter[] = [
          { code: 'N705' },
          { code: 'N706', coefficient: -1 },
          { code: 'N707', coefficient: -1 },
        ];
        if (isYear) {
          return this.divide(
            this.sumByCode(calcResultMap, itemValues, N708Filter, year, monthlyIndex, 部門pk, true),
            denominatorValue,
            division
          );
        }
        return this.divide(
          this.sumByCode(calcResultMap, itemValues, N708Filter, year, monthlyIndex, 部門pk),
          denominatorValue,
          division
        );
      default:
        return this.divide(
          numeratorValue,
          code === 'N512' ? new BigNumber(denominatorValue).div(100).toNumber() : denominatorValue,
          division
        );
    }
  }

  /**
   * 計算用のPlbscfItemValueを取得
   * @param typeList
   * @param year
   * @param 部門pk
   * @returns
   */
  private getFilteredItemValues(typeList: PlbscfTypeList[], year: number, 部門pk?: number): PlbscfItemValue[] {
    return this.filterYearValuesByYear(this.filterYearValues(typeList, 部門pk), year);
  }

  /**
   * value/monthlyValues に、0でない値が存在するか
   * @param value
   * @param monthlyValues
   * @returns
   */
  private isNotZeroValue(value?: number, monthlyValues?: number[]): boolean {
    if (monthlyValues !== undefined) {
      for (const mv of monthlyValues) {
        if (mv !== 0) return true;
      }
    }
    if (value !== undefined && value !== 0) return true;
    return false;
  }
}
